/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  Box,
  Button,
  DialogContentText,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  CustomFilterV2,
  DialogDefaultV2,
  ExpandableTableV2,
  TableV2,
} from '../../../components/v2';
import { tableV2CellContentSx } from '../../../components/v2/tableV2Styles';
import * as actionsEquipamentos from '../../../store/equipamento/actions';
import * as Roles from '../../../constants/role';
import * as selectorsAuth from '../../../store/auth/reducer';
import Formulario from './Formulario';
import EquipamentosUnidadesDetail from './EquipamentosUnidadesDetail';
import EquipamentoStatusDialog from './EquipamentoStatusDialog';
import { fetchEquipamentosGrouped } from './fetchEquipamentosGrouped';
import {
  buildEquipamentoFilter,
  getPosseContagens,
  grupoEquipamentoId,
  isFiltroIdentidade,
} from './helpers';

const QUERY_EQUIPAMENTO_ID = 'equipamentoId';

const getEquipamentoIdFromSearch = (search) => {
  const params = new URLSearchParams(search || '');
  const raw = params.get(QUERY_EQUIPAMENTO_ID) || params.get('id');
  return raw && String(raw).trim() ? String(raw).trim() : null;
};

const EquipamentosV2 = ({
  deleteEquipamento,
  authData,
  exportEquipamento,
  history,
  location,
}) => {
  const [open, setOpen] = useState(false);
  const [id, setId] = useState(null);
  const [openExport, setOpenExport] = useState(false);
  const [localFilter, setLocalFilter] = useState(() => {
    const params = new URLSearchParams(location?.search || '');
    const tipoEquipamento = params.get('tipoEquipamento');
    const posseEletropontes = params.get('posseEletropontes');
    return {
      ...(tipoEquipamento ? { tipoEquipamento } : {}),
      ...(posseEletropontes === 'true' || posseEletropontes === 'false'
        ? { posseEletropontes }
        : {}),
    };
  });
  const [openFilter, setOpenFilter] = useState(false);
  const [reload, setReload] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [statusGrupo, setStatusGrupo] = useState(null);

  const isAdmin = () =>
    authData && (authData.role === Roles.ADMIN || authData.role === Roles.SUPER_ADMIN);

  const useIdentidade = isFiltroIdentidade(localFilter);

  const loadGrupos = useCallback(
    (page, pageSize, filterData) => fetchEquipamentosGrouped(page, pageSize, filterData),
    []
  );

  useEffect(() => {
    const deepLinkId = getEquipamentoIdFromSearch(location?.search);
    if (!deepLinkId) return;
    if (deepLinkId === 'novo') {
      setId(null);
      setOpen(true);
      return;
    }
    setId(deepLinkId);
    setOpen(true);
  }, [location?.search]);

  const replaceSearch = (mutate) => {
    const params = new URLSearchParams(location.search);
    mutate(params);
    const nextSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
    });
  };

  const setEquipamentoIdInUrl = (equipamentoId) => {
    replaceSearch((params) => {
      params.delete('id');
      if (equipamentoId == null || equipamentoId === '') {
        params.delete(QUERY_EQUIPAMENTO_ID);
        return;
      }
      params.set(QUERY_EQUIPAMENTO_ID, String(equipamentoId));
    });
  };

  const openEquipamento = (rowId) => {
    setOpen(true);
    setId(rowId);
    setEquipamentoIdInUrl(rowId);
  };

  const onAdd = () => {
    setOpen(true);
    setId(null);
    setEquipamentoIdInUrl('novo');
  };

  const refreshStatusGrupo = useCallback(async () => {
    if (!statusGrupo) return;
    try {
      const { rows } = await fetchEquipamentosGrouped(0, 50, {
        ...localFilter,
        tipoEquipamento: statusGrupo.tipoEquipamento || localFilter.tipoEquipamento,
        tipoEquipamentos: statusGrupo.tipoEquipamento
          ? [statusGrupo.tipoEquipamento]
          : localFilter.tipoEquipamentos,
        modelos: statusGrupo.modelo ? [statusGrupo.modelo] : localFilter.modelos,
      });
      const currentId = grupoEquipamentoId(statusGrupo);
      const updated = (rows || []).find((row) => grupoEquipamentoId(row) === currentId);
      if (updated) {
        setStatusGrupo(updated);
      }
    } catch (e) {
      // mantém o grupo atual se o refresh falhar
    }
  }, [statusGrupo, localFilter]);

  const onClose = (isSave) => {
    if (isSave) {
      setReload((current) => !current);
      refreshStatusGrupo();
    }
    setOpen(false);
    setId(null);
    setEquipamentoIdInUrl(null);
  };

  const onDeleteUnit = (rowId) => {
    setDeleteId(rowId);
  };

  const confirmDelete = () => {
    if (!deleteId) return;
    deleteEquipamento(deleteId, () => {
      setReload((current) => !current);
      setDeleteId(null);
    });
  };

  const headerActions = [];
  if (isAdmin()) {
    headerActions.push({
      label: 'Novo',
      onClick: onAdd,
      startIcon: <AddIcon />,
      variant: 'contained',
    });
    headerActions.push({
      label: 'Exportar',
      onClick: () => setOpenExport(true),
      startIcon: <FileDownloadOutlinedIcon />,
      variant: 'outlined',
    });
  }

  const groupedColumns = useMemo(() => [
    {
      id: 'expand',
      header: '',
      size: 48,
      cell: ({ row }) => (
        row.getCanExpand() ? (
          <IconButton
            size="small"
            aria-label={row.getIsExpanded() ? 'Recolher unidades' : 'Expandir unidades'}
            aria-expanded={row.getIsExpanded()}
            onClick={row.getToggleExpandedHandler()}
          >
            {row.getIsExpanded() ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </IconButton>
        ) : null
      ),
    },
    {
      accessorKey: 'tipoEquipamentoNome',
      header: 'Tipo',
      size: 220,
      minSize: 140,
      cell: ({ row }) => (
        <Box component="span" sx={tableV2CellContentSx}>
          {row.original.tipoEquipamentoNome}
        </Box>
      ),
    },
    {
      accessorKey: 'modelo',
      header: 'Modelo',
      size: 160,
      minSize: 100,
      cell: ({ row }) => (
        <Box component="span" sx={tableV2CellContentSx}>
          {row.original.modelo || '—'}
        </Box>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      size: 220,
      minSize: 180,
      cell: ({ row }) => {
        const quantidade = row.original.quantidade ?? 0;
        const { comEmpresa, comColaborador } = getPosseContagens(row.original);
        return (
          <Box
            role="button"
            tabIndex={0}
            aria-label="Abrir status de posse"
            onClick={(event) => {
              event.stopPropagation();
              setStatusGrupo(row.original);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                setStatusGrupo(row.original);
              }
            }}
            sx={{
              ...tableV2CellContentSx,
              display: 'inline-block',
              width: 'fit-content',
              maxWidth: '100%',
              cursor: 'pointer',
              borderRadius: 1,
              px: 0.5,
              py: 0.25,
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <Box component="span" sx={{ fontWeight: 600, display: 'block' }}>
              {quantidade} total
            </Box>
            <Box
              component="span"
              sx={{
                display: 'block',
                color: 'text.secondary',
                fontSize: '0.75rem',
                lineHeight: 1.4,
              }}
            >
              {comEmpresa} Eletropontes · {comColaborador} com Colaborador
            </Box>
          </Box>
        );
      },
    },
  ], []);

  const unitColumns = [
    { field: 'tipoEquipamentoNome', headerName: 'Tipo de Equipamento', flex: 0.8 },
    { field: 'numPatrimonio', headerName: 'Num. Patrimônio', flex: 0.5 },
    { field: 'modelo', headerName: 'Modelo', flex: 0.6 },
    { field: 'numSerie', headerName: 'Num. Série', flex: 0.5 },
    { field: 'codigoSisant', headerName: 'Código SISANT', flex: 0.5 },
    { field: 'codigoAnatel', headerName: 'Código ANATEL', flex: 0.5 },
    { field: 'ultimoRepasse', headerName: 'Último Repasse/Colaborador', flex: 0.8 },
    { field: 'categoriaRepasse', headerName: 'Categoria Repasse', flex: 0.5 },
  ];

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Typography variant="h4">Equipamentos</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        {useIdentidade ? (
          <TableV2
            useApi
            fetchUrl="equipamentos/filtro"
            filterData={localFilter}
            refreshKey={reload}
            columns={unitColumns}
            rowHeight={52}
            onEdit={isAdmin() ? openEquipamento : undefined}
            onView={openEquipamento}
            onDelete={isAdmin() ? (rowId) => {
              deleteEquipamento(rowId, () => setReload((current) => !current));
            } : undefined}
            headerActions={headerActions}
          />
        ) : (
          <ExpandableTableV2
            fetchData={loadGrupos}
            filterData={localFilter}
            refreshKey={reload}
            rowHeight={56}
            headerActions={headerActions}
            singleExpand
            getRowCanExpand={(row) =>
              (row.original.unidades?.length ?? 0) > 0
              || (row.original.quantidade ?? 0) > 0
            }
            getRowId={(row) => grupoEquipamentoId(row)}
            renderSubComponent={({ row }) => (
              <EquipamentosUnidadesDetail
                unidades={row.original.unidades}
                tipoNome={row.original.tipoEquipamentoNome}
                modelo={row.original.modelo}
                canManage={isAdmin()}
                onOpen={openEquipamento}
                onDelete={onDeleteUnit}
              />
            )}
            columns={groupedColumns}
          />
        )}

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          appliedFilter={localFilter}
          fields={[
            { field: 'useTipoEquipamento', md: 6, xs: 12, limitTags: 2 },
            { field: 'useEquipamentoModelos', md: 6, xs: 12 },
            { field: 'useNumPatrimonio', md: 6, xs: 12 },
            { field: 'useNumSerie', md: 6, xs: 12 },
            { field: 'useColaborador', md: 6, xs: 12, multiSelect: true },
            { field: 'useSisant', md: 6, xs: 12 },
            { field: 'usePosseEletropontes', md: 6, xs: 12 },
            { field: 'useAtivo', md: 6, xs: 12 },
          ]}
          onFilter={(filterData) => {
            setLocalFilter(buildEquipamentoFilter(filterData));
            setOpenFilter(false);
          }}
        />

        <Formulario
          id={id}
          open={open}
          onClose={onClose}
          zIndex={statusGrupo ? 1400 : undefined}
        />

        <EquipamentoStatusDialog
          open={!!statusGrupo}
          grupo={statusGrupo}
          onClose={() => {
            if (open) return;
            setStatusGrupo(null);
          }}
          onOpenUnidade={openEquipamento}
          zIndex={1300}
        />

        <DialogDefaultV2
          open={openExport}
          handlerCancel={() => setOpenExport(false)}
          handlerConfirm={() => {
            exportEquipamento(localFilter, () => {
              setOpenExport(false);
            });
          }}
          title="Confirmação"
          confirmMessage="Sim"
          cancelMessage="Não"
        >
          <DialogContentText>
            Deseja baixar todas as informações filtradas?
          </DialogContentText>
        </DialogDefaultV2>

        <DialogDefaultV2
          open={!!deleteId}
          handlerCancel={() => setDeleteId(null)}
          handlerConfirm={confirmDelete}
          title="Confirmar exclusão"
          confirmMessage="Deletar"
          cancelMessage="Cancelar"
        >
          <DialogContentText>
            A informação será removida e não poderá ser mais recuperada.
          </DialogContentText>
        </DialogDefaultV2>
      </Stack>
    </Box>
  );
};

EquipamentosV2.propTypes = {
  deleteEquipamento: PropTypes.func.isRequired,
  exportEquipamento: PropTypes.func.isRequired,
  authData: PropTypes.object,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...actionsEquipamentos,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(EquipamentosV2));
